import React, { useState, useMemo, useCallback, useId } from 'react';
import { Button } from '../common';
import { Heading, Text } from '../common/Typography';
import {
  parseAliasInput,
  formatAliasesForInput,
  savePaymentSearchAliases,
} from '../../services/clubMemberPaymentAliasService';

function canonicalAliasesKey(parsedArray) {
  return [...parsedArray].map(String).sort().join('\0');
}

function savedAliasesCanonical(saved) {
  const list = (saved || []).map((s) => String(s).trim()).filter(Boolean);
  return [...new Set(list)].sort().join('\0');
}

/**
 * Host/admin: edit bank/transfer name hints for payment search (Payment Tracking & session wrap-up).
 * Parent should pass key={playerId} so draft resets when switching members.
 */
function PaymentAliasHintsEditor({
  clubId,
  playerId,
  initialAliases,
  onSaved,
  compact = false,
  className = '',
}) {
  const statusId = useId();
  const [draft, setDraft] = useState(() => formatAliasesForInput(initialAliases || []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAck, setSavedAck] = useState(false);

  const savedHintsList = useMemo(() => {
    if (!Array.isArray(initialAliases)) return [];
    return [...new Set(initialAliases.map((s) => String(s).trim()).filter(Boolean))];
  }, [initialAliases]);

  const dirty = useMemo(() => {
    if (!playerId) return false;
    const draftKey = canonicalAliasesKey(parseAliasInput(draft));
    const savedKey = savedAliasesCanonical(initialAliases);
    return draftKey !== savedKey;
  }, [playerId, initialAliases, draft]);

  const handleSave = useCallback(async () => {
    if (!clubId || !playerId || !dirty) return;
    setSaving(true);
    setError(null);
    const res = await savePaymentSearchAliases(clubId, playerId, draft);
    setSaving(false);
    if (!res.success) {
      setError(res.error || 'Could not save aliases.');
      setSavedAck(false);
      return;
    }
    setSavedAck(true);
    onSaved?.(res.aliases || []);
  }, [clubId, playerId, draft, dirty, onSaved]);

  if (!playerId || !clubId) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {compact ? (
        <Text className="text-xs text-text-muted">
          Names as on your bank feed—one per line or comma-separated. Used when recording payments; not shown to players.
        </Text>
      ) : (
        <>
          <div>
            <Heading level={4} className="text-sm mb-1">Bank / transfer name hints (host only)</Heading>
            <Text className="text-xs text-text-muted">
              Names as they appear on your bank feed—one per line or comma-separated. Used only here to find members when recording payments; not shown to players.
            </Text>
          </div>
        </>
      )}
      <div
        className="rounded-md border border-border-medium bg-surface-subtle/80 px-3 py-2"
        aria-label="Saved payment name hints"
      >
        <Text className="text-xs font-semibold text-text-secondary mb-1.5">Saved payment name hints</Text>
        {savedHintsList.length > 0 ? (
          <ul className="text-sm text-text-primary space-y-1 list-disc list-inside pl-0.5 m-0">
            {savedHintsList.map((hint) => (
              <li key={hint}>{hint}</li>
            ))}
          </ul>
        ) : (
          <Text className="text-xs text-text-muted m-0">
            None yet — add names below as they appear on your bank feed.
          </Text>
        )}
      </div>
      <textarea
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setSavedAck(false);
          setError(null);
        }}
        rows={compact ? 3 : 4}
        className={`w-full px-3 py-2 text-sm rounded-md border bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-text-muted ${
          dirty ? 'border-amber-500/60 ring-1 ring-amber-500/20' : 'border-border-medium'
        }`}
        placeholder={'e.g. J SMITH\nJane Smith Joint Acc'}
        disabled={saving}
        aria-describedby={statusId}
      />
      <div className="flex flex-wrap items-center gap-2" id={statusId}>
        <Button
          type="button"
          variant="secondary"
          size="small"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save hints'}
        </Button>
        {!dirty && savedAck && (
          <Text
            className="text-xs font-medium text-success-dark"
            role="status"
            aria-live="polite"
          >
            Saved — your hints are stored for this member.
          </Text>
        )}
        {error && (
          <Text className="text-xs text-danger-dark" role="alert">
            {error}
          </Text>
        )}
        {dirty && !saving && (
          <Text className="text-xs text-text-muted">Unsaved changes</Text>
        )}
      </div>
    </div>
  );
}

export default React.memo(PaymentAliasHintsEditor);
